import type { Action } from "svelte/action";

/**
 * Reveal-on-scroll. Adds the `is-in` class the first time the node enters the
 * viewport, then unobserves it. Pair with the `.reveal` CSS in app.css.
 * An optional stagger delay (ms) can be passed.
 */
export const inview: Action<HTMLElement, { delay?: number } | undefined> = (
  node,
  params,
) => {
  const delay = params?.delay ?? 0;
  if (delay) node.style.setProperty("--reveal-delay", `${delay}ms`);

  // Respect reduced-motion: reveal immediately, skip the observer.
  const reduce =
    typeof matchMedia !== "undefined" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduce) {
    node.classList.add("is-in");
    return;
  }

  const obs = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          node.classList.add("is-in");
          obs.unobserve(node);
        }
      }
    },
    { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
  );
  obs.observe(node);

  return {
    destroy() {
      obs.disconnect();
    },
  };
};

/** Smooth-scroll to the anchor target of an in-page link, with header offset. */
export const smoothScroll: Action<HTMLAnchorElement> = (node) => {
  const handler = (e: MouseEvent) => {
    const href = node.getAttribute("href");
    if (!href || !href.startsWith("#")) return;
    const target = document.querySelector(href);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    history.replaceState(null, "", href);
  };
  node.addEventListener("click", handler);
  return {
    destroy() {
      node.removeEventListener("click", handler);
    },
  };
};
